/**
 * Governance types for Gravity Bridge proposals
 * Matches backend API response format (snake_case)
 */

export enum ProposalStatus {
  UNSPECIFIED = 0,
  DEPOSIT_PERIOD = 1,
  VOTING_PERIOD = 2,
  PASSED = 3,
  REJECTED = 4,
  FAILED = 5,
}

export enum VoteOption {
  UNSPECIFIED = 0,
  YES = 1,
  ABSTAIN = 2,
  NO = 3,
  NO_WITH_VETO = 4,
}

export interface Coin {
  denom: string;
  amount: string;
}

export interface TallyResult {
  yes: string;
  abstain: string;
  no: string;
  no_with_veto: string;
}

export interface ProposalContent {
  type_url: string;
  title: string;
  description: string;
}

export interface ProposalInfo {
  proposal_id: number;
  content: ProposalContent | null;
  status: number;
  final_tally_result: TallyResult | null;
  submit_time: string | null;
  deposit_end_time: string | null;
  total_deposit: Coin[];
  voting_start_time: string | null;
  voting_end_time: string | null;
}

export interface Vote {
  proposal_id: string;
  voter: string;
  option: VoteOption;
  options: WeightedVoteOption[];
}

export interface WeightedVoteOption {
  option: VoteOption;
  weight: string;
}

export interface Deposit {
  proposal_id: string;
  depositor: string;
  amount: Coin[];
}

/**
 * Helper functions for proposal status
 */
export function getProposalStatusLabel(status: number): string {
  const statusEnum = status as ProposalStatus;
  switch (statusEnum) {
    case ProposalStatus.DEPOSIT_PERIOD:
      return 'Deposit Period';
    case ProposalStatus.VOTING_PERIOD:
      return 'Voting';
    case ProposalStatus.PASSED:
      return 'Passed';
    case ProposalStatus.REJECTED:
      return 'Rejected';
    case ProposalStatus.FAILED:
      return 'Failed';
    default:
      return 'Unknown';
  }
}

export function getProposalStatusColor(status: number): string {
  const statusEnum = status as ProposalStatus;
  switch (statusEnum) {
    case ProposalStatus.DEPOSIT_PERIOD:
      return 'bg-yellow-100 text-yellow-800';
    case ProposalStatus.VOTING_PERIOD:
      return 'bg-blue-100 text-blue-800';
    case ProposalStatus.PASSED:
      return 'bg-green-100 text-green-800';
    case ProposalStatus.REJECTED:
      return 'bg-red-100 text-red-800';
    case ProposalStatus.FAILED:
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

export function getVoteOptionLabel(option: VoteOption): string {
  switch (option) {
    case VoteOption.YES:
      return 'Yes';
    case VoteOption.ABSTAIN:
      return 'Abstain';
    case VoteOption.NO:
      return 'No';
    case VoteOption.NO_WITH_VETO:
      return 'No with Veto';
    default:
      return 'Unknown';
  }
}

export function isProposalActive(proposal: ProposalInfo): boolean {
  return (proposal.status as ProposalStatus) === ProposalStatus.VOTING_PERIOD;
}

/**
 * Calculate vote percentages from tally result
 */
export function calculateVotePercentages(tally: TallyResult | null): {
  yes: number;
  abstain: number;
  no: number;
  noWithVeto: number;
  total: string;
} {
  if (!tally) {
    return { yes: 0, abstain: 0, no: 0, noWithVeto: 0, total: '0' };
  }

  const yes = BigInt(tally.yes || '0');
  const abstain = BigInt(tally.abstain || '0');
  const no = BigInt(tally.no || '0');
  const noWithVeto = BigInt(tally.no_with_veto || '0');
  const total = yes + abstain + no + noWithVeto;

  if (total === 0n) {
    return { yes: 0, abstain: 0, no: 0, noWithVeto: 0, total: '0' };
  }

  return {
    yes: Number((yes * 10000n) / total) / 100,
    abstain: Number((abstain * 10000n) / total) / 100,
    no: Number((no * 10000n) / total) / 100,
    noWithVeto: Number((noWithVeto * 10000n) / total) / 100,
    total: total.toString(),
  };
}
